import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, LogOut, Plus } from "lucide-react";

import { useAuth } from "@/contexts/auth";
import BrandLogo from "@/components/navbar/BrandLogo";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { ADMIN_NAV_ITEMS, isAdminPathActive } from "./adminNavigation";

const NAV_GROUP_ORDER = ["Overview", "Moderation", "Management", "Tools"] as const;

const AdminSidebar = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();

  const groupedItems = NAV_GROUP_ORDER.map((group) => ({
    group,
    items: ADMIN_NAV_ITEMS.filter((item) => item.group === group),
  })).filter((group) => group.items.length > 0);

  return (
    <Sidebar className="border-r border-gray-200 bg-white" collapsible="offcanvas" variant="sidebar">
      <SidebarHeader className="gap-0 border-b border-gray-200 px-4 py-4">
        <Link to="/admin" className="block">
          <BrandLogo className="origin-left scale-90" to={null} />
        </Link>
        <div className="mt-4">
          <Button
            asChild
            className="h-10 w-full justify-start rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
          >
            <Link to="/admin/create-listing">
              <Plus className="h-4 w-4" />
              Create listing
            </Link>
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-3">
        {groupedItems.map(({ group, items }) => (
          <SidebarGroup key={group} className="px-0 py-1">
            <SidebarGroupLabel className="px-3 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-500">
              {group}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map((item) => {
                  const active = isAdminPathActive(location.pathname, item.path);

                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                        className={cn(
                          "h-10 rounded-lg px-3 text-gray-700 hover:bg-gray-50 hover:text-whatsapp",
                          "data-[active=true]:bg-whatsapp-light data-[active=true]:text-whatsapp data-[active=true]:shadow-[inset_0_0_0_1px_rgba(37,211,102,0.22)]",
                        )}
                      >
                        <Link to={item.path} className="flex items-center gap-3">
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="truncate text-sm font-medium">{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarSeparator className="mx-4" />

      <SidebarFooter className="gap-3 px-4 py-4">
        <div className="px-1">
          <p className="truncate text-sm font-medium text-gray-800">
            {user?.email ?? "Admin"}
          </p>
        </div>

        <Button
          asChild
          variant="outline"
          className="h-10 justify-start rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
        >
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Return to site
          </Link>
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="h-10 justify-start rounded-lg text-gray-600 hover:bg-gray-50 hover:text-whatsapp"
          onClick={() => {
            void signOut();
          }}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AdminSidebar;
